/*
  Quick view of the product cards (snippets/product-quickview.liquid: the
  empty <dialog>; sections/quick-view.liquid: its content;
  assets/quick-view.css). Vanilla, defer, on every page while it's on
  (layout/theme.liquid).
  - Opens with the cards' eye (.action-plus of product-grid-item.liquid and
    the like, whose markup doesn't change): its onclick calls
    quiqview(handle), defined here. Its Bootstrap data-toggle="modal" finds
    no #quickViewModal and does nothing. Without <dialog> (Safari < 15.4),
    it goes to the product.
  - A <dialog> opened with showModal() (above everything, the page behind
    inert), the mini cart's look: centered on desktop, a sheet from the
    bottom on phones. The X, Esc and the backdrop close it; the focus goes
    back to the eye. html.qv-open locks the page's scroll.
  - The content: /products/<handle>?section_id=quick-view, drawn by
    Shopify with the product and the customer's session (prices and
    promotions from the cards' snippets), kept while the page is open.
    After drawing it: the hearts (Wishlist.activate); compare.js marks the
    "Comparar" link by itself (its MutationObserver).
  - Gallery: one photo per view, a native horizontal scroll with snap
    points; arrows and thumbnails on desktop, swipe and dots on phones
    (window.DotPager, theme.js.liquid); ← → Inicio and Fin on the photos.
    Past 4 thumbnails, theirs is a carousel too: its arrows move 4 at a
    time, and the active one is scrolled into view when the photo changes.
  - The description is cut by height (CSS): .is-clamped, its fade, only
    when it's taller than that (also once its images load).
  - "Agregar al carrito" and "Comprar ahora" carry the cards' onclick
    (Shopify.addItem), so assets/cart-button.js gives them the cards' states
    (spinner, check, red) and puts an error in the view
    ([data-cart-notice-slot]). Added from "Agregar al carrito": the view
    closes and the mini cart opens ("Al agregar una pieza", mini-cart.js).
    "Comprar ahora" passes QuickView.checkout to Shopify.addItem: once the
    piece is in the cart, the checkout, with the whole cart.
*/
(function () {
  'use strict';

  var SECTION = 'quick-view';
  var ADD_URL = /\/cart\/add(\.js)?(\?|$)/;
  var root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
  var supported = typeof window.HTMLDialogElement === 'function';
  var reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var cache = {};
  var current = '';
  var adding = false; // "Agregar al carrito" of the view, on its way
  var lastFocus = null;

  function dialog() {
    return document.getElementById('QuickView');
  }

  function productUrl(handle) {
    return root + 'products/' + encodeURIComponent(handle);
  }

  function fetchView(handle) {
    if (cache[handle]) return Promise.resolve(cache[handle]);
    return fetch(productUrl(handle) + '?section_id=' + SECTION, { credentials: 'same-origin' })
      .then(function (response) {
        if (!response.ok) return null;
        return response.text().then(function (html) {
          var el = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-quick-view]');
          if (el) cache[handle] = el;
          return el;
        });
      })
      .catch(function () { return null; });
  }

  // ---- Open and close ----------------------------------------------------------

  function open(handle) {
    if (!handle) return;
    var d = dialog();
    if (!d || !supported) {
      window.location.href = productUrl(handle);
      return;
    }
    current = handle;
    adding = false;
    var body = d.querySelector('[data-qv-body]');
    body.setAttribute('aria-busy', 'true');
    body.innerHTML = '<div class="qv-loading" aria-hidden="true"><span class="qv-loading__media"></span><span class="qv-loading__text"></span></div>';
    d.removeAttribute('aria-labelledby');
    if (!d.open) {
      lastFocus = document.activeElement;
      d.showModal();
      document.documentElement.classList.add('qv-open');
      var x = d.querySelector('.qv-dialog__close');
      if (x) x.focus({ preventScroll: true });
      void d.offsetWidth; // closed position first, then the slide
      d.classList.add('is-open');
    }
    fetchView(handle).then(function (el) {
      if (current !== handle || !d.open) return;
      body.removeAttribute('aria-busy');
      body.innerHTML = '';
      if (!el) {
        var p = document.createElement('p');
        p.className = 'qv-dialog__error';
        p.textContent = d.getAttribute('data-qv-error') || '';
        body.appendChild(p);
        return;
      }
      body.appendChild(el.cloneNode(true));
      if (body.querySelector('#QuickViewTitle')) d.setAttribute('aria-labelledby', 'QuickViewTitle');
      if (window.Wishlist && Wishlist.activate) Wishlist.activate(body);
      var gallery = body.querySelector('[data-qv-gallery]');
      if (gallery) initGallery(gallery);
      var desc = body.querySelector('[data-qv-desc]');
      if (desc) clampDesc(desc);
    });
  }

  function close(now) {
    var d = dialog();
    if (!d || !d.open) return;
    d.classList.remove('is-open');
    if (now || reduceMotion) {
      d.close();
      return;
    }
    window.setTimeout(function () {
      if (d.open && !d.classList.contains('is-open')) d.close();
    }, 300);
  }

  // Esc: animated like the X. 'close' (also when the browser closes it at
  // once): the cleanup. Neither event bubbles: listened in capture.
  document.addEventListener('cancel', function (event) {
    if (event.target !== dialog()) return;
    event.preventDefault();
    close();
  }, true);
  document.addEventListener('close', function (event) {
    if (event.target !== dialog()) return;
    event.target.classList.remove('is-open');
    document.documentElement.classList.remove('qv-open');
    current = '';
    adding = false;
    if (lastFocus && lastFocus.focus && document.contains(lastFocus)) lastFocus.focus({ preventScroll: true });
    lastFocus = null;
  }, true);

  // ---- Description ---------------------------------------------------------------

  function clampDesc(desc) {
    function check() {
      desc.classList.toggle('is-clamped', desc.scrollHeight > desc.clientHeight + 1);
    }
    check();
    Array.prototype.forEach.call(desc.querySelectorAll('img'), function (img) {
      if (!img.complete) img.addEventListener('load', check, { once: true });
    });
  }

  // ---- Gallery -----------------------------------------------------------------

  function initGallery(gallery) {
    var track = gallery.querySelector('[data-pager-track]');
    if (!track) return;
    var total = track.children.length;
    var thumbs = gallery.querySelectorAll('[data-qv-thumb]');
    var prev = gallery.querySelector('[data-qv-prev]');
    var next = gallery.querySelector('[data-qv-next]');
    var thumbTrack = gallery.querySelector('[data-qv-thumbs]');
    var thumbPrev = gallery.querySelector('[data-qv-thumbs-prev]');
    var thumbNext = gallery.querySelector('[data-qv-thumbs-next]');
    var pager = window.DotPager ? new window.DotPager(gallery) : null;
    var behavior = reduceMotion ? 'auto' : 'smooth';
    var ticking = false;
    var shown = 0;

    function index() {
      if (pager) return pager.current();
      return Math.round(track.scrollLeft / (track.clientWidth || 1));
    }

    function go(i) {
      i = Math.max(0, Math.min(total - 1, i));
      if (pager) {
        pager.goTo(i);
        return;
      }
      track.scrollTo({ left: i * track.clientWidth, behavior: behavior });
    }

    // The thumbnails' carousel (past 4): its arrows, and the active one in view
    function syncThumbArrows() {
      if (!thumbTrack) return;
      var max = thumbTrack.scrollWidth - thumbTrack.clientWidth;
      if (thumbPrev) thumbPrev.setAttribute('aria-disabled', thumbTrack.scrollLeft <= 1 ? 'true' : 'false');
      if (thumbNext) thumbNext.setAttribute('aria-disabled', thumbTrack.scrollLeft >= max - 1 ? 'true' : 'false');
    }

    function showThumb(i) {
      var thumb = thumbs[i];
      if (!thumb || !thumbTrack || thumbTrack.scrollWidth <= thumbTrack.clientWidth) return;
      var left = thumb.offsetLeft;
      var right = left + thumb.offsetWidth;
      if (left < thumbTrack.scrollLeft) {
        thumbTrack.scrollTo({ left: left, behavior: behavior });
      } else if (right > thumbTrack.scrollLeft + thumbTrack.clientWidth) {
        thumbTrack.scrollTo({ left: right - thumbTrack.clientWidth, behavior: behavior });
      }
    }

    function moveThumbs(dir) {
      var gap = parseFloat(window.getComputedStyle(thumbTrack).columnGap) || 0;
      thumbTrack.scrollBy({ left: dir * (thumbTrack.clientWidth + gap), behavior: behavior });
    }

    function sync() {
      var i = index();
      Array.prototype.forEach.call(thumbs, function (thumb, n) {
        if (n === i) {
          thumb.setAttribute('aria-current', 'true');
        } else {
          thumb.removeAttribute('aria-current');
        }
      });
      if (prev) prev.setAttribute('aria-disabled', i <= 0 ? 'true' : 'false');
      if (next) next.setAttribute('aria-disabled', i >= total - 1 ? 'true' : 'false');
      // Only when the photo changes: scrolling the thumbnails by hand stays
      if (i !== shown) {
        shown = i;
        showThumb(i);
      }
    }

    track.addEventListener('scroll', function () {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(function () {
        ticking = false;
        sync();
      });
    }, { passive: true });

    if (prev) prev.addEventListener('click', function () { go(index() - 1); });
    if (next) next.addEventListener('click', function () { go(index() + 1); });
    Array.prototype.forEach.call(thumbs, function (thumb) {
      thumb.addEventListener('click', function () {
        go(parseInt(thumb.getAttribute('data-qv-thumb'), 10) || 0);
      });
    });
    if (thumbTrack) {
      thumbTrack.addEventListener('scroll', syncThumbArrows, { passive: true });
      if (thumbPrev) thumbPrev.addEventListener('click', function () { moveThumbs(-1); });
      if (thumbNext) thumbNext.addEventListener('click', function () { moveThumbs(1); });
      syncThumbArrows();
    }

    track.addEventListener('keydown', function (event) {
      var to = null;
      if (event.key === 'ArrowLeft') to = index() - 1;
      if (event.key === 'ArrowRight') to = index() + 1;
      if (event.key === 'Home') to = 0;
      if (event.key === 'End') to = total - 1;
      if (to === null) return;
      event.preventDefault();
      go(to);
    });

    sync();
  }

  // ---- Clicks and the cart -----------------------------------------------------

  document.addEventListener('click', function (event) {
    var target = event.target;
    if (!target.closest) return;
    if (target.closest('[data-qv-close]')) {
      close();
      return;
    }
    // Its onclick already asked for the piece (cart-button.js stops a click
    // while it's loading, in capture, so this doesn't run then)
    if (target.closest('#QuickView [data-qv-add][onclick]')) adding = true;
  });

  if (window.jQuery) {
    jQuery(document).on('ajaxSuccess', function (event, xhr, settings) {
      if (!adding || !settings || !ADD_URL.test(settings.url || '')) return;
      adding = false;
      close();
    });
    jQuery(document).on('ajaxError', function (event, xhr, settings) {
      if (settings && ADD_URL.test(settings.url || '')) adding = false;
    });
  }

  window.QuickView = {
    open: open,
    close: close,
    // "Comprar ahora": Shopify.addItem's callback, once the piece is in the
    // cart (cart.api.js then skips its window and its cart refresh)
    checkout: function () {
      window.location.href = root + 'checkout';
    }
  };

  // The cards' onclick (product-grid-item.liquid and the like)
  window.quiqview = open;
})();
